import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Dispatch } from 'redux'
import { acknowledgeTradeSubmission, clearAccountSearch, searchAccounts } from '../../actions'
import { usePermissions, useUrlPathTerm } from '../../hooks'
import { RootState } from '../../store'
import { AccountSearchState } from '../../types'
import { RoutesEnum } from '../../types/RoutesEnum'
import { isPrimitiveEmpty } from '../ApexLogin'
import { PermissionsEnum } from '../contexts/permissions/constants'
import { AccountSearchContext, AccountSearchProps } from '../contexts/search/AccountSearchContext'

interface AccountSearchProviderProps {
  accountSearchState: AccountSearchState
  acknowledgeTradeSubmission: () => void
  children: JSX.Element | JSX.Element[]
  clearAccountSearch: () => void
  searchAccounts: (searchTerm: string) => void
}

const AccountSearchProviderComponent = ({
  accountSearchState,
  acknowledgeTradeSubmission,
  children,
  clearAccountSearch,
  searchAccounts,
}: AccountSearchProviderProps) => {
  const history = useHistory()
  const urlSearchTerm = useUrlPathTerm()
  const { hasPermission, permissionsLoading } = usePermissions()
  const canEnterTrade = hasPermission(PermissionsEnum.TradeReportsModify)

  useEffect(() => {
    if (urlSearchTerm && !permissionsLoading) {
      if (canEnterTrade) {
        searchAccounts(urlSearchTerm)
      } else {
        history.replace('/')
      }
    }
    // eslint-disable-next-line
  }, [canEnterTrade, permissionsLoading])

  useEffect(() => {
    const { accountResults, loadingResults, searchTerm } = accountSearchState

    const changePath = (tradePath: string) => {
      if (history.location.pathname !== tradePath) {
        acknowledgeTradeSubmission()
        history.replace('/')
        history.push({ pathname: tradePath })
      }
    }

    if (!loadingResults) {
      if (accountResults.length === 1) {
        const accountNumber = accountResults[0]?.accountNumber
        if (!isPrimitiveEmpty(accountNumber)) {
          changePath(`/${RoutesEnum.TradeEntry}/${accountNumber}`)
        }
      } else if (searchTerm) {
        changePath(`/${RoutesEnum.Results}/${searchTerm}`)
      }
    }
    // eslint-disable-next-line
  }, [accountSearchState])

  useEffect(() => {
    const { searchTerm } = accountSearchState
    if (urlSearchTerm.length === 0 && searchTerm) {
      clearAccountSearch()
    }
    // eslint-disable-next-line
  }, [urlSearchTerm])

  const accountSearch: AccountSearchProps = {
    accountSearchState,
    searchAccounts,
  }
  return <AccountSearchContext.Provider value={accountSearch}>{children}</AccountSearchContext.Provider>
}

const mapStateToProps = (state: RootState) => ({ accountSearchState: state.accountsReducer })

const mapDispatchToProps = (dispatch: Dispatch) => ({
  acknowledgeTradeSubmission: () => acknowledgeTradeSubmission(dispatch),
  searchAccounts: (searchTerm: string) => searchAccounts(dispatch, searchTerm),
  clearAccountSearch: () => clearAccountSearch(dispatch),
})

export const AccountSearchProvider = connect(mapStateToProps, mapDispatchToProps)(AccountSearchProviderComponent)
